import { Injectable, Optional } from "@angular/core";
import { Logger } from "../../logger";
import {
  GraphicsInfo,
  GraphicsInfoColorField,
  GraphicsInfoField,
  GraphicsInfoFieldType,
  GraphicsInfoMediaField,
  GraphicsInfoPositionField,
  GraphicsInfoScaleField,
  GraphicsInfoTextField,
} from "../../../domain/graphics-info";
import { RectangleF } from "../../../domain/rectangle-f";

/** Parse "HH:mm:ss" or "H:mm:ss" to milliseconds. Returns undefined if invalid. */
function parseDurationToMs(duration: string | undefined): number | undefined {
  if (!duration || typeof duration !== "string") return undefined;
  const parts = duration.trim().split(":").map(Number);
  if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return undefined;
  const [h, m, s] = parts;
  return (h * 3600 + m * 60 + s) * 1000;
}

function toGraphicsInfo(apiGraphicsInfo, isTemplate): GraphicsInfo {
  let fields;

  if (apiGraphicsInfo.fields) {
    fields = apiGraphicsInfo.fields.map(extractField);
  }
  let overrideGraphicPosition: RectangleF = null;
  if (apiGraphicsInfo.OvveridePositionRectangleF) {
    const responseRectangleF = JSON.parse(
      apiGraphicsInfo.OvveridePositionRectangleF,
    );
    overrideGraphicPosition = new RectangleF(
      parseFloat(responseRectangleF.OffsetX),
      parseFloat(responseRectangleF.OffsetY),
      parseFloat(responseRectangleF.Width),
      parseFloat(responseRectangleF.Height),
    );
  }
  const durationMS =
    apiGraphicsInfo.durationMS ??
    apiGraphicsInfo.duration_ms ??
    parseDurationToMs(apiGraphicsInfo.duration) ??
    0;
  return new GraphicsInfo(
    isTemplate,
    apiGraphicsInfo.id,
    apiGraphicsInfo.uid,
    apiGraphicsInfo.TemplateId,
    apiGraphicsInfo.name,
    apiGraphicsInfo.description,
    apiGraphicsInfo.thumbnail_url,
    apiGraphicsInfo.sample_video_url,
    apiGraphicsInfo.startLoopMS,
    apiGraphicsInfo.endLoopMS,
    durationMS,
    apiGraphicsInfo.zLayer ?? 30,
    fields,
    overrideGraphicPosition,
  );
}

function extractField(dbField): GraphicsInfoField {
  switch (dbField.type) {
    case GraphicsInfoFieldType.Text: {
      return new GraphicsInfoTextField(
        dbField.id,
        dbField.label,
        dbField.value.text,
        dbField.value.link,
        dbField.value["font_id"],
        dbField.value.size,
        dbField.value.color,
        dbField.value.align,
        dbField.value.dynamic_options,
        dbField.value.shrink_to_fit,
        dbField.value.is_ticker || false,
        dbField.value.ticker_speed || 0,
        dbField.value.ticker_direction || 0,
        dbField.value.ticker_is_loop || false,
      );
    }
    case GraphicsInfoFieldType.Media: {
      return new GraphicsInfoMediaField(
        dbField.id,
        dbField.label,
        dbField.value.media_id,
        dbField.value.mediaType,
        dbField.value.link,
        dbField.value.dynamic,
        dbField.value.fit_media,
        dbField.value.image_or_video,
      );
    }
    case GraphicsInfoFieldType.Color: {
      return new GraphicsInfoColorField(
        dbField.id,
        dbField.label,
        dbField.value,
      );
    }
    case GraphicsInfoFieldType.Scale: {
      return new GraphicsInfoScaleField(dbField.id, dbField.label, dbField.value);
    }
    case GraphicsInfoFieldType.Position: {
      return new GraphicsInfoPositionField(
        dbField.id,
        dbField.label,
        dbField.value,
      );
    }
    default:
      throw new Error("unknown field " + dbField.type);
  }
}

export function toJson(field: GraphicsInfoField) {
  if (field instanceof GraphicsInfoTextField) {
    return {
      text: field.text,
      link: field.link,
      font_id: field.fontId,
      size: field.size,
      color: field.color,
      align: field.align,
      dynamic_options: field.dynamicOptions,
      shrink_to_fit: field.shrinkToFit,
      is_ticker: field.isTicker,
      ticker_speed: field.tickerSpeed,
      ticker_direction: field.tickerDirection,
      ticker_is_loop: field.tickerIsLoop,
    };
  } else if (field instanceof GraphicsInfoMediaField) {
    return {
      media_id: field.mediaId,
      mediaType: field.mediaType,
      link: field.link,
      dynamic: field.dynamic,
      fit_media: field.fitMedia,
      image_or_video: field.imageOrVideo,
    };
  } else if (
    field instanceof GraphicsInfoColorField ||
    field instanceof GraphicsInfoScaleField ||
    field instanceof GraphicsInfoPositionField
  ) {
    return field.value;
  }
}

@Injectable()
export class GraphicsMapper {
  constructor(@Optional() private logger: Logger) {}

  mapGetGraphicInfoResponse(
    graphicsInfo: any,
    isTemplate: boolean,
  ): Promise<GraphicsInfo> {
    return Promise.resolve(toGraphicsInfo(graphicsInfo, isTemplate));
  }

  mapSaveGraphicInfoRequest(graphicsInfo: GraphicsInfo) {
    let graphicId, graphicUid, templateId, templateUid;
    if (graphicsInfo.isTemplate) {
      templateId = graphicsInfo.id;
      templateUid = graphicsInfo.uid;
    } else {
      graphicId = graphicsInfo.id;
      graphicUid = graphicsInfo.uid;
    }
    const fields = graphicsInfo.fields.map((field) => {
      return { id: field.id, value: toJson(field) };
    });
    return {
      id: graphicId,
      uid: graphicUid,
      template_id: templateId,
      template_uid: templateUid,
      name: graphicsInfo.name,
      description: graphicsInfo.description,
      thumbnail_url: graphicsInfo.thumbnail,
      duration_ms: graphicsInfo.durationMS,
      fields: fields,
      z_layer: graphicsInfo.zLayer,
      override_position_rectangle_f: graphicsInfo.overrideGraphicPosition,
    };
  }

  mapGetAllGraphicsResponse(allGraphics: any): Promise<GraphicsInfo[]> {
    const self = this;
    const templates =
      allGraphics.Templates && allGraphics.Templates.length > 0
        ? allGraphics.Templates
        : [];
    const graphics =
      allGraphics.Graphics && allGraphics.Graphics.length > 0
        ? allGraphics.Graphics
        : [];

    const mappedTemplates = templates.map((x) => toGraphicsInfo(x, true));
    const mappedGraphics = graphics.map((x) => toGraphicsInfo(x, false));

    return Promise.resolve(mappedTemplates.concat(mappedGraphics));
  }
}
